import { verifyEmail } from "api/auth";
import { ApiError } from "api/client";
import { Button, Card } from "components";
import AccountsContainer from "pages/accounts-onboarding/components/accounts-container";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Paths from "routes/Paths";
import { useSession } from "Session";

import styles from "./VerifyEmail.module.css";

type VerifyStatus = "verifying" | "success" | "error";

const titleKeys: Record<VerifyStatus, string> = {
	verifying: "verifyEmail.verifyingTitle",
	success: "verifyEmail.successTitle",
	error: "verifyEmail.errorTitle"
};

export const VerifyEmailPage = () => {
	const [params] = useSearchParams();
	const token = params.get("token") ?? "";
	const navigate = useNavigate();
	const { hasSession, refreshUser } = useSession();
	const { t } = useTranslation("accountsPage");

	const [status, setStatus] = useState<VerifyStatus>("verifying");
	// The outcome is held as a namespace key rather than resolved copy: verification starts on mount,
	// which can be before the namespace has loaded, and a resolved string would freeze the raw key.
	const [messageKey, setMessageKey] = useState("verifyEmail.verifyingMessage");
	// Set only when the API explains the failure itself — that text is already in its final form.
	const [apiMessage, setApiMessage] = useState<string | null>(null);
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) {
			return;
		}
		hasRun.current = true;

		if (!token) {
			setStatus("error");
			setMessageKey("verifyEmail.missingToken");

			return;
		}

		verifyEmail(token)
			.then(async () => {
				setStatus("success");
				setMessageKey("verifyEmail.successMessage");
				if (hasSession) {
					await refreshUser();
				}
			})
			.catch((error: unknown) => {
				setStatus("error");
				setMessageKey("verifyEmail.error");
				if (error instanceof ApiError) {
					setApiMessage(error.message);
				}
			});
	}, [token, hasSession, refreshUser]);

	const destination = hasSession ? Paths.root : Paths.signIn;

	const onSubmit = useCallback(() => navigate(destination, { replace: true }), [destination, navigate]);

	return (
		<AccountsContainer>
			<Card className={styles.card}>
				<div className={styles.content}>
					<h1 className={styles.title}>{t(titleKeys[status])}</h1>
					<p className={styles.message}>{apiMessage ?? t(messageKey)}</p>
					{status !== "verifying" && (
						<Button className={styles.action} onClick={onSubmit} type="button" fullWidth>
							{hasSession ? t("verifyEmail.goToTrees") : t("verifyEmail.goToSignIn")}
						</Button>
					)}
				</div>
			</Card>
		</AccountsContainer>
	);
};
